import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchOrgBranding } from '../api/orgBranding.ts';
import { useSession } from '../auth/sessionContext.ts';
import { useTheme } from './themeContext.ts';

/**
 * The bridge between session data and `ThemeProvider.tsx`'s DOM-facing state (`BACKLOG.md` B4).
 *
 * `ThemeProvider` is mounted in `main.tsx`, above the router and therefore above any session — it
 * has to be, since colour mode/accent apply on the **login page**, before a session exists
 * (`preference.ts`'s own header). Org branding is the opposite: it cannot be known until a session
 * says which practice this is. This component is the seam between the two: mounted only where a
 * session is guaranteed (`RequireAuth.tsx`'s `<SessionContext.Provider>`, a sibling of its own
 * `children`, not a wrapper around them — it renders nothing itself), it fetches the caller's own
 * practice branding and pushes it into `ThemeProvider` via `setOrgBranding`, the one function that
 * exists for exactly this.
 *
 * The query key includes `session.orgId` rather than being a bare constant, specifically for
 * `POST /api/auth/switch-org` (`BACKLOG.md` T1): switching practices changes `session.orgId` without
 * unmounting `RequireAuth`'s `<SessionContext.Provider>` (only the session *data* changes), so a
 * key with no `orgId` in it would keep serving the previous practice's cached branding after a
 * switch. `staleTime` matches `RequireAuth.tsx`'s own session query — branding changes rarely enough
 * that a minute of staleness is an acceptable trade against refetching on every navigation.
 */
export const orgBrandingQueryKey = (orgId: string) => ['orgBranding', orgId] as const;

export function OrgBrandingSync() {
  const session = useSession();
  const { setOrgBranding } = useTheme();
  const query = useQuery({
    queryKey: orgBrandingQueryKey(session.orgId),
    queryFn: ({ signal }) => fetchOrgBranding(signal),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data !== undefined) setOrgBranding(query.data);
    // A fetch failure (transient network loss, a 5xx) leaves the theme showing whatever it already
    // had — the closed-enum default on first load, or the last-known branding on a refetch — rather
    // than clearing to "no branding" and flashing the default over a real practice colour for a
    // failure that will very likely resolve on its own retry.
  }, [query.data, setOrgBranding]);

  // Reset on unmount — the practice's colour must not linger once `RequireAuth` tears this down
  // (logout, or a session that stops resolving), which would otherwise leave a previous account's
  // brand colour showing on the login page until the next practice's own fetch overwrites it.
  useEffect(() => {
    return () => {
      setOrgBranding(null);
    };
  }, [setOrgBranding]);

  return null;
}
